
(function () {
    "use strict";

    if (window.__customAbFacetsLoaded) return;
    window.__customAbFacetsLoaded = true;

    /* ------------------------------------------------------------------------
     1. Constants
     ------------------------------------------------------------------------ */

    const SELECTORS = {
        root: "[data-custom-ab-root]",
        form: "[data-custom-ab-form]",
        results: "[data-custom-ab-results]",
        panel: "[data-custom-ab-panel]",
        overlay: "[data-custom-ab-overlay]",
        open: "[data-custom-ab-open]",
        close: "[data-custom-ab-close]",
        apply: "[data-custom-ab-apply]",
        clear: "[data-custom-ab-clear]",
        link: "[data-custom-ab-link]",
        range: "[data-custom-ab-range]",
    };

    const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

    // How long typing in a price field pauses before the filter applies.
    const PRICE_DEBOUNCE_MS = 600;

    // Must match --custom-ab-accordion-ms in the stylesheet.
    const ACCORDION_MS = 280;

    // Fallback grid selectors, used only when an install omitted the
    // .custom-ab-results wrapper. Override with data-custom-ab-results-selector.
    const FALLBACK_GRID = ".collection-matrix, .search-matrix, .product-list";

    /* ------------------------------------------------------------------------
     2. Helpers
     ------------------------------------------------------------------------ */

    // Returns a wrapped version of fn that only runs after it has NOT been
    // called for `waitMs` — used so fast typing doesn't fire a fetch per key.
    function debounce(fn, waitMs) {
        let timer;
        return function () {
            const args = arguments;
            clearTimeout(timer);
            timer = setTimeout(function () {
                fn.apply(null, args);
            }, waitMs);
        };
    }

    function toNumber(value, fallback) {
        const n = parseFloat(value);
        return isNaN(n) ? fallback : n;
    }

    // Form state -> clean query string. Empty values and `page` are dropped so
    // changing a filter always lands the shopper on page 1.
    function formToQuery(form) {
        const params = new URLSearchParams(new FormData(form));
        const cleaned = new URLSearchParams();
        params.forEach(function (value, key) {
            if (value === null || String(value).trim() === "") return;
            if (key === "page") return;
            cleaned.append(key, value);
        });
        return cleaned.toString();
    }

    /* ------------------------------------------------------------------------
     3. Scroll lock — one drawer open at a time. position:fixed rather than
        overflow:hidden, because iOS Safari ignores the latter on <body>.
     ------------------------------------------------------------------------ */

    let scrollLockY = 0;
    let scrollLockActive = false;

    function lockBodyScroll() {
        if (scrollLockActive) return;
        scrollLockY = window.pageYOffset || document.documentElement.scrollTop || 0;
        document.body.style.top = "-" + scrollLockY + "px";
        document.body.classList.add("custom-ab-scroll-locked");
        scrollLockActive = true;
    }

    function unlockBodyScroll() {
        if (!scrollLockActive) return;
        document.body.classList.remove("custom-ab-scroll-locked");
        document.body.style.top = "";
        window.scrollTo(0, scrollLockY);
        scrollLockActive = false;
    }

    /* ------------------------------------------------------------------------
     4. setupFacets — everything for one filter module on the page
     ------------------------------------------------------------------------ */

    function setupFacets(root) {
        /* ---------- 4.1 State + lookups ---------- */

        const sectionId = root.getAttribute("data-custom-ab-section");

        let drawerIsOpen = false; //   is the mobile drawer showing right now?
        let lastFocused = null; //     element to give focus back to on close
        let pendingScroll = false; //  scroll once the drawer closes
        let fetchController = null; // in-flight fetch, aborted by newer requests
        let fetchToken = 0; //         guards against stale responses winning

        const applyDebounced = debounce(applyFromForm, PRICE_DEBOUNCE_MS);

        // The form and results are re-queried on demand because their contents
        // are replaced on every update.
        function getForm() {
            return root.querySelector(SELECTORS.form);
        }
        function getPanel() {
            return root.querySelector(SELECTORS.panel);
        }
        function getResults() {
            return root.querySelector(SELECTORS.results);
        }

        /* ---------- 4.2 Events — everything is wired here ---------- */

        function bindEvents() {
            // Capture phase: InstantClick binds on bubble, so capturing lets us
            // stop it before it turns an AJAX click into a full page navigation.
            root.addEventListener("click", onRootClick, true);

            root.addEventListener("change", function (event) {
                const el = event.target;
                if (!el) return;
                if (el.matches('input[type="checkbox"][name]') || el.matches('select[name="sort_by"]') || el.matches("[data-custom-ab-range-min], [data-custom-ab-range-max]")) {
                    // Checkbox toggled, sort picked, or a slider handle released.
                    applyFromForm();
                }
            });

            root.addEventListener("input", function (event) {
                const el = event.target;
                if (!el) return;
                if (el.matches("[data-custom-ab-range-min], [data-custom-ab-range-max]")) {
                    onRangeInput(el);
                } else if (el.matches("[data-custom-ab-price]")) {
                    onPriceInput(el);
                    applyDebounced();
                }
            });

            root.addEventListener("submit", function (event) {
                if (event.target && event.target.matches(SELECTORS.form)) {
                    event.preventDefault();
                    closeDrawer();
                    applyFromForm();
                }
            });

            document.addEventListener("keydown", onKeydown);
            // Backstop for the overlay: closes the drawer on any tap outside it.
            document.addEventListener("click", onOutsideClick);
            window.addEventListener("popstate", function () {
                render(window.location.href, false);
            });
            window.addEventListener("resize", debounce(onResize, 150));
        }

        /* ---------- 4.3 Clicks — one handler decides what was clicked ---------- */

        function onRootClick(event) {
            const target = event.target;
            if (!target || !target.closest) return;

            if (target.closest(SELECTORS.open)) {
                event.preventDefault();
                openDrawer();
                return;
            }

            if (target.closest(SELECTORS.close) || target.closest(SELECTORS.overlay)) {
                event.preventDefault();
                closeDrawer();
                return;
            }

            if (target.closest(SELECTORS.apply)) {
                // Results are already live; Apply just dismisses the drawer.
                event.preventDefault();
                closeDrawer();
                return;
            }

            if (target.closest(SELECTORS.clear)) {
                event.preventDefault();
                clearAll();
                return;
            }

            const summary = target.closest(".custom-ab-group__summary");
            if (summary) {
                event.preventDefault();
                toggleGroup(summary);
                return;
            }

            // Active-filter chips and clear links.
            const chip = target.closest(SELECTORS.link);
            if (chip && chip.href) {
                event.preventDefault();
                event.stopPropagation();
                render(chip.href, true, true);
                return;
            }

            // Pagination links inside the results. Matching on `page=` rather than
            // a wrapper class keeps this working with any pagination markup.
            const results = getResults();
            if (!results || !results.contains(target)) return;

            const link = target.closest("a[href]");
            if (!link) return;
            // The theme's own load-more and our module's button opt out here.
            if (link.closest(".js-load-more") || link.hasAttribute("data-custom-ab-ignore")) return;
            if (link.href.indexOf("page=") === -1) return;
            if (link.hostname !== window.location.hostname) return;

            event.preventDefault();
            event.stopPropagation();
            render(link.href, true, true);
        }

        function onOutsideClick(event) {
            if (!drawerIsOpen) return;

            const panel = getPanel();
            const target = event.target;
            if (!panel || !target || !target.closest) return;

            if (panel.contains(target)) return; //         inside the drawer
            if (target.closest(SELECTORS.open)) return; // the trigger that opened it

            closeDrawer();
        }

        function onKeydown(event) {
            if (!drawerIsOpen) return;
            if (event.key === "Escape" || event.keyCode === 27) {
                event.preventDefault();
                closeDrawer();
            } else if (event.key === "Tab" || event.keyCode === 9) {
                trapFocus(event);
            }
        }

        function onResize() {
            // The trigger is display:none once the sidebar layout kicks in; if the
            // viewport grew while the drawer was open, drop the modal state.
            if (!drawerIsOpen) return;
            const trigger = root.querySelector(SELECTORS.open);
            if (trigger && trigger.offsetParent === null) closeDrawer();
        }

        /* ---------- 4.4 Drawer ---------- */

        function openDrawer() {
            const panel = getPanel();
            const overlay = root.querySelector(SELECTORS.overlay);
            if (!panel) return;

            lastFocused = document.activeElement;
            drawerIsOpen = true;

            panel.classList.add("is-open");
            if (overlay) {
                overlay.classList.add("is-open");
                overlay.setAttribute("aria-hidden", "false");
            }

            const trigger = root.querySelector(SELECTORS.open);
            if (trigger) trigger.setAttribute("aria-expanded", "true");

            lockBodyScroll();

            // The panel is visibility:hidden until is-open is style-resolved, and
            // .focus() is a no-op on a non-rendered element — so wait a frame.
            const closeBtn = panel.querySelector(SELECTORS.close);
            if (closeBtn) {
                requestAnimationFrame(function () {
                    closeBtn.focus();
                });
            }
        }

        function closeDrawer() {
            if (!drawerIsOpen) return;

            const panel = getPanel();
            const overlay = root.querySelector(SELECTORS.overlay);

            drawerIsOpen = false;
            if (panel) panel.classList.remove("is-open");
            if (overlay) {
                overlay.classList.remove("is-open");
                overlay.setAttribute("aria-hidden", "true");
            }

            const trigger = root.querySelector(SELECTORS.open);
            if (trigger) trigger.setAttribute("aria-expanded", "false");

            unlockBodyScroll();

            if (lastFocused && lastFocused.focus) {
                lastFocused.focus();
            } else if (trigger) {
                trigger.focus();
            }

            // A filter changed while the drawer was open — now that the scroll
            // lock is gone, glide to the refreshed results.
            if (pendingScroll) {
                pendingScroll = false;
                scrollToResults();
            }
        }

        // Keep Tab / Shift+Tab inside the open drawer.
        function trapFocus(event) {
            const panel = getPanel();
            if (!panel) return;

            const nodes = Array.from(panel.querySelectorAll(FOCUSABLE)).filter(function (el) {
                return el.offsetParent !== null;
            });
            if (!nodes.length) return;

            const first = nodes[0];
            const last = nodes[nodes.length - 1];

            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        }

        /* ---------- 4.5 Accordion ----------
       A <details> can't animate natively: removing `open` hides the content
       instantly, so the closing frame never renders. Keep `open` set until
       the height transition finishes, then drop it. Heights are cleared
       afterwards so the group can reflow on its own. */

        function toggleGroup(summary) {
            const group = summary.closest(".custom-ab-group");
            if (!group) return;

            const content = group.querySelector(".custom-ab-group__content");
            const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
            if (!content || reduceMotion) {
                group.open = !group.open;
                return;
            }

            // A second click mid-animation must not leave a stale timer behind.
            if (content.customAbTimer) {
                clearTimeout(content.customAbTimer);
                content.customAbTimer = null;
            }

            if (group.open) {
                // Closing: fix the height, force a reflow, then transition to 0.
                content.style.height = content.scrollHeight + "px";
                content.offsetHeight;
                content.style.height = "0px";
                content.customAbTimer = setTimeout(function () {
                    group.open = false;
                    content.style.height = "";
                    content.customAbTimer = null;
                }, ACCORDION_MS);
            } else {
                // Opening: start at 0 and transition up to the content's height.
                group.open = true;
                content.style.height = "0px";
                content.offsetHeight;
                content.style.height = content.scrollHeight + "px";
                content.customAbTimer = setTimeout(function () {
                    content.style.height = "";
                    content.customAbTimer = null;
                }, ACCORDION_MS);
            }
        }

        /* ---------- 4.6 Price slider ----------
       Two overlaid <input type=range> drive the visual handles; the number
       inputs carry the real filter params. Each side mirrors the other. */

        function paintAllRanges() {
            root.querySelectorAll(SELECTORS.range).forEach(paintRange);
        }

        // Position the green fill between the two handle centres. A native range
        // thumb travels between thumbWidth/2 and width-thumbWidth/2, not
        // 0..width — match that geometry so the fill doesn't drift at the ends.
        function paintRange(wrap) {
            const minEl = wrap.querySelector("[data-custom-ab-range-min]");
            const maxEl = wrap.querySelector("[data-custom-ab-range-max]");
            const fill = wrap.querySelector("[data-custom-ab-range-fill]");
            if (!minEl || !maxEl || !fill) return;

            const hi = toNumber(wrap.getAttribute("data-range-max"), 0);
            if (hi <= 0) return;

            const a = toNumber(minEl.value, 0);
            const b = toNumber(maxEl.value, hi);

            const width = wrap.getBoundingClientRect().width;
            const thumb = 18;

            if (!width) {
                // Not laid out yet (e.g. inside a closed group) — percentages do.
                fill.style.left = (a / hi) * 100 + "%";
                fill.style.right = 100 - (b / hi) * 100 + "%";
                return;
            }

            const usable = width - thumb;
            const leftPx = thumb / 2 + (a / hi) * usable;
            const rightPx = thumb / 2 + (b / hi) * usable;

            fill.style.left = leftPx + "px";
            fill.style.right = width - rightPx + "px";
        }

        // Dragging a handle: keep the two from crossing, then mirror into the
        // number inputs so the form carries the real filter params.
        function onRangeInput(input) {
            const wrap = input.closest(SELECTORS.range);
            if (!wrap) return;

            const minEl = wrap.querySelector("[data-custom-ab-range-min]");
            const maxEl = wrap.querySelector("[data-custom-ab-range-max]");
            const hi = toNumber(wrap.getAttribute("data-range-max"), 0);
            let a = toNumber(minEl.value, 0);
            let b = toNumber(maxEl.value, hi);

            if (a > b) {
                if (input === minEl) {
                    a = b;
                    minEl.value = a;
                } else {
                    b = a;
                    maxEl.value = b;
                }
            }

            const group = wrap.parentNode;
            const priceMin = group.querySelector("[data-custom-ab-price-min]");
            const priceMax = group.querySelector("[data-custom-ab-price-max]");

            // Blank a field once its handle is back at the extreme, so no price
            // param is submitted and the filter reads as cleared. The epsilon
            // absorbs float error — an exact compare leaves a phantom filter.
            if (priceMin) priceMin.value = a <= 0 ? "" : a;
            if (priceMax) priceMax.value = b >= hi - 0.01 ? "" : b;

            paintRange(wrap);
        }

        // Typing in a number input: mirror back onto the handles.
        function onPriceInput(input) {
            const group = input.closest(".custom-ab-group__content") || root;
            const wrap = group.querySelector(SELECTORS.range);
            if (!wrap) return;

            const hi = toNumber(wrap.getAttribute("data-range-max"), 0);
            const minEl = wrap.querySelector("[data-custom-ab-range-min]");
            const maxEl = wrap.querySelector("[data-custom-ab-range-max]");

            if (input.hasAttribute("data-custom-ab-price-min") && minEl) {
                minEl.value = toNumber(input.value, 0);
            } else if (input.hasAttribute("data-custom-ab-price-max") && maxEl) {
                maxEl.value = toNumber(input.value, hi);
            }

            paintRange(wrap);
        }

        /* ---------- 4.7 Fetch + swap ---------- */

        // Read the form and render the matching URL.
        function applyFromForm() {
            const form = getForm();
            if (!form) return;

            const action = form.getAttribute("action") || window.location.pathname;
            const pathname = action.split("?")[0];
            const query = formToQuery(form);

            // Every filter change scrolls the results back into view.
            render(query ? pathname + "?" + query : pathname, true, true);
        }

        function clearAll() {
            const form = getForm();
            if (!form) return;

            form.querySelectorAll('input[type="checkbox"]').forEach(function (input) {
                input.checked = false;
            });
            form.querySelectorAll("[data-custom-ab-price]").forEach(function (input) {
                input.value = "";
            });
            form.querySelectorAll(SELECTORS.range).forEach(function (wrap) {
                const hi = toNumber(wrap.getAttribute("data-range-max"), 0);
                const minEl = wrap.querySelector("[data-custom-ab-range-min]");
                const maxEl = wrap.querySelector("[data-custom-ab-range-max]");
                if (minEl) minEl.value = 0;
                if (maxEl) maxEl.value = hi;
            });

            paintAllRanges();
            applyFromForm();
        }

        // Fetch the section for `url` and swap the new markup in.
        function render(url, pushState, scrollToTop) {
            if (fetchController) fetchController.abort();
            fetchController = typeof AbortController !== "undefined" ? new AbortController() : null;

            // Token guard: an aborted request settles *after* its replacement has
            // already set is-loading, so only the newest request may clear it.
            const token = ++fetchToken;

            const target = new URL(url, window.location.origin);
            const cleanUrl = target.pathname + target.search;

            const fetchUrl = new URL(target.href);
            fetchUrl.searchParams.set("section_id", sectionId);

            root.classList.add("is-loading");

            const options = { headers: { "X-Requested-With": "XMLHttpRequest" } };
            if (fetchController) options.signal = fetchController.signal;

            fetch(fetchUrl.toString(), options)
                .then(function (response) {
                    if (!response.ok) throw new Error("Section render failed: " + response.status);
                    return response.text();
                })
                .then(function (html) {
                    if (token !== fetchToken) return;
                    swap(html, cleanUrl, pushState, scrollToTop);
                })
                .catch(function (error) {
                    if (error && error.name === "AbortError") return;
                    if (token !== fetchToken) return;
                    // Never leave the shopper stuck — fall back to a real navigation.
                    window.location.href = cleanUrl;
                })
                .finally(function () {
                    if (token !== fetchToken) return;
                    root.classList.remove("is-loading");
                    fetchController = null;
                });
        }

        // Put the freshly rendered results + filter panel into the page.
        function swap(html, cleanUrl, pushState, scrollToTop) {
            const parsed = new DOMParser().parseFromString(html, "text/html");

            const newResults = parsed.querySelector(SELECTORS.results);
            const currentResults = getResults();

            if (!newResults || !currentResults) {
                window.location.href = cleanUrl;
                return;
            }

            currentResults.innerHTML = newResults.innerHTML;

            // Filter panel — counts and available values change with every
            // selection. Preserve each group's open/closed state so the panel
            // doesn't visually reset under the shopper's finger.
            const newForm = parsed.querySelector(SELECTORS.form);
            const currentForm = getForm();

            if (newForm && currentForm) {
                const openState = Array.from(currentForm.querySelectorAll("details.custom-ab-group"), function (group) {
                    return group.open;
                });

                currentForm.innerHTML = newForm.innerHTML;

                currentForm.querySelectorAll("details.custom-ab-group").forEach(function (group, i) {
                    if (typeof openState[i] === "boolean") group.open = openState[i];
                });

                // innerHTML replacement drops the panel's open class — reapply.
                if (drawerIsOpen) {
                    const panel = getPanel();
                    const overlay = root.querySelector(SELECTORS.overlay);
                    if (panel) panel.classList.add("is-open");
                    if (overlay) {
                        overlay.classList.add("is-open");
                        overlay.setAttribute("aria-hidden", "false");
                    }
                    const trigger = root.querySelector(SELECTORS.open);
                    if (trigger) trigger.setAttribute("aria-expanded", "true");
                }
            }

            markLinks();
            paintAllRanges();

            if (pushState && window.history && window.history.pushState) {
                window.history.pushState({ customAbFacets: true }, "", cleanUrl);
            }

            if (scrollToTop) {
                if (drawerIsOpen) {
                    // The body is scroll-locked while the drawer is open; scrolling
                    // now would be undone when closeDrawer() restores the position.
                    pendingScroll = true;
                } else {
                    scrollToResults();
                }
            }

            document.dispatchEvent(
                new CustomEvent("custom-ab:updated", {
                    bubbles: true,
                    detail: { root: root, url: cleanUrl },
                }),
            );

            reinitPagination();

            // Nudge lazy-loaders and grid scripts that only listen for these.
            window.dispatchEvent(new Event("resize"));
            window.dispatchEvent(new Event("scroll"));
        }

        /* ---------- 4.8 Scrolling ---------- */

        // Glide to the top of the whole filter + grid section (the root, not
        // just the results div), landing right below the banner. The offset is
        // the sticky header height measured at init — a hard-coded value either
        // cuts the section off under the header or leaves a random gap.
        function scrollToResults() {
            const measured = parseFloat(window.getComputedStyle(root).getPropertyValue("--custom-ab-sticky-top"));
            const offset = (isNaN(measured) ? 80 : measured) + 10;

            const top = root.getBoundingClientRect().top + window.pageYOffset - offset;
            window.scrollTo({
                top: top < 0 ? 0 : top,
                behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
            });
        }

        /* ---------- 4.9 Interop with the host theme ---------- */

        // InstantClick binds a capture-phase click listener on `document`, which
        // runs before ours and would turn an AJAX click into a full page load.
        // It skips anything carrying data-no-instant, so tag our links.
        function markLinks() {
            const results = getResults();
            if (results) {
                results.querySelectorAll('a[href*="page="]').forEach(function (link) {
                    if (link.closest(".js-load-more")) return;
                    link.setAttribute("data-no-instant", "");
                });
            }
            root.querySelectorAll(SELECTORS.link).forEach(function (link) {
                link.setAttribute("data-no-instant", "");
            });
        }

        // The explicit .custom-ab-results wrapper is the supported install. When
        // it's missing, build one around the theme's own grid so the module
        // still works.
        function ensureResults() {
            if (getResults()) return;

            const section = root.closest('[id^="shopify-section-"]') || document;
            const selector = root.getAttribute("data-custom-ab-results-selector") || FALLBACK_GRID;
            const grid = section.querySelector(selector);

            if (!grid || root.contains(grid)) return;

            const wrap = document.createElement("div");
            wrap.className = "custom-ab-results";
            wrap.setAttribute("data-custom-ab-results", "");

            // Take the grid plus the pagination bits that follow it.
            const nodes = [grid];
            let next = grid.nextElementSibling;
            while (next) {
                if (next.matches(".load-more__icon, .js-load-more, .paginate") || next.querySelector(".paginate, .js-load-more")) {
                    nodes.push(next);
                    next = next.nextElementSibling;
                } else {
                    break;
                }
            }

            nodes.forEach(function (node) {
                wrap.appendChild(node);
            });
            root.appendChild(wrap);
        }

        // The desktop sidebar is sticky. Themes pin their own header, so measure
        // it and pin the card below it rather than hard-coding a per-store
        // offset. The result is shared with scrollToResults via a CSS variable.
        function measureStickyOffset() {
            let tallest = 0;
            const candidates = document.querySelectorAll('header, [class*="header" i], [id*="header" i], [data-sticky-header]');

            candidates.forEach(function (el) {
                const cs = window.getComputedStyle(el);
                if (cs.position !== "sticky" && cs.position !== "fixed") return;

                // Read the declared offset rather than the current rect: at scroll 0
                // a sticky header hasn't travelled to its pinned position yet.
                const stickTop = parseFloat(cs.top);
                if (isNaN(stickTop) || stickTop > 60) return;

                const height = el.getBoundingClientRect().height;
                if (!height) return;

                tallest = Math.max(tallest, stickTop + height);
            });

            // Ignore implausible measurements rather than pushing the card off-screen.
            if (tallest > 0 && tallest < window.innerHeight * 0.5) {
                root.style.setProperty("--custom-ab-sticky-top", Math.round(tallest) + 20 + "px");
            }
        }

        // Turbo's infinite scroll captures the grid element when it builds its
        // Waypoint, so swapping the grid leaves that Waypoint bound to a
        // detached node. Re-init it against the new element.
        //
        // Deliberately NOT re-calling enableLoadMoreProducts/enableLoadMoreSearch:
        // those attach delegated handlers to <body>, which survive the swap —
        // calling them again would stack handlers and load each page twice.
        function reinitPagination() {
            if (root.getAttribute("data-custom-ab-pagination") !== "infinite_scroll") return;

            const results = getResults();
            if (!results) return;

            if (results.querySelector(".collection-matrix") && typeof window.enableInfiniteScroll === "function") {
                window.enableInfiniteScroll();
            } else if (results.querySelector(".search-matrix") && typeof window.enableInfiniteSearchScroll === "function") {
                window.enableInfiniteSearchScroll();
            }
        }

        /* ---------- Start this instance ---------- */

        ensureResults();
        bindEvents();
        markLinks();
        paintAllRanges();
        measureStickyOffset();
        root.setAttribute("data-custom-ab-init", "true");
    }

    /* ------------------------------------------------------------------------
     5. Boot — once per root, resilient to theme editor reloads
     ------------------------------------------------------------------------ */

    function init() {
        document.querySelectorAll(SELECTORS.root).forEach(function (root) {
            if (root.getAttribute("data-custom-ab-init") === "true") return;
            setupFacets(root);
        });
    }

    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", init);
    } else {
        init();
    }

    document.addEventListener("shopify:section:load", init);
    document.addEventListener("page:loaded", init);
})();
